import React from 'react';
import CustomMedia from 'util/custom-media-query';
import FormDialogrenderer from './FormDialogRenderer';
import i18n from 'i18n';
import { css } from 'styled-components';
import {
  applyStyles,
  replaceHyphenAndSpaces,
  convertDoubleWidthNumber,
  convertKana,
  allowExactKeysOnly
} from 'util/helpers';
import Button from 'components/common/Button';
import TextField from 'components/common/TextField';
import SelectField from 'components/common/SelectField';
import Image from 'components/common/Image';
import imgInvoicePC1x from 'assets/images/PC/@1x/img_Invoice.png';
import imgInvoicePC2x from 'assets/images/PC/@2x/img_Invoice@2x.png';
import imgInvoiceSP2x from 'assets/images/SP/@2x/img_Invoice@2x.png';
import imgInvoiceSP3x from 'assets/images/SP/@3x/img_Invoice@3x.png';
import Prefectures from 'constants/prefectures';
import {
  required,
  mustBeNumber,
  kanaPattern,
  postcodePattern,
  composeValidators,
  justLength
} from 'validation/validators';
import axios from 'axios';

const handleClickInsertAddress = async form => {
  const { batch, getState, change } = form;
  const postcode = getState().values.receiverPostcode;

  axios
    .get(
      `${
        process.env.REACT_APP_YGETS_API_BASE_URL
      }/api/address?postalCode=${postcode}`,
      { timeout: 3000 }
    )
    .then(response => {
      const data = response.data;

      if (Object.keys(data).length === 0) {
        return;
      }

      batch(() => {
        change('receiverPrefecture', data.prefecture);
        change('receiverCity', data.city);
      });
    });
};

const handleBlurPostcode = (form, value) => {
  const { change } = form;
  const replaced = convertDoubleWidthNumber(value);
  const formatted = replaceHyphenAndSpaces(replaced);

  change('receiverPostcode', formatted);
  handleChangePostCode(form);
};

const handleBlurNumbers = (form, key, value) => {
  const { change } = form;
  const replaced = convertDoubleWidthNumber(value);
  const formatted = replaceHyphenAndSpaces(replaced);

  change(key, formatted);
};

const handleBlurKana = (form, value) => {
  const { change } = form;
  const replaced = convertKana(value);

  change('receiverNameKana', replaced);
};

const handleChangePostCode = form => {
  const fieldState = form.getFieldState('receiverPostcode');

  if (fieldState.valid) {
    handleClickInsertAddress(form);
  }
};

const DialogBody = ({
  fetchAddress,
  form,
  referringDataTemplate,
  isConfirm
}) => (
  <div className="receiver">
    <div className="image-wrapper">
      <Image
        pc1x={imgInvoicePC1x}
        pc2x={imgInvoicePC2x}
        sp2x={imgInvoiceSP2x}
        sp3x={imgInvoiceSP3x}
        alt="img_invoice"
      />
    </div>
    <div className="fields-container">
      {isConfirm ? (
        <div className="order-content">{referringDataTemplate()}</div>
      ) : (
        <React.Fragment>
          <TextField
            name="receiverName"
            validate={value => composeValidators(required)(value)}
            label={i18n.t('orderAggregators:common:order.name')}
            maxLength={30}
            placeholder={i18n.t(
              'orderAggregators:common:order.namePlaceholder'
            )}
            required={true}
            notes={[i18n.t('orderAggregators:common:order.nameNotes')]}
          />
          <TextField
            name="receiverNameKana"
            validate={value => kanaPattern(value)}
            label={i18n.t('orderAggregators:common:order.kana')}
            maxLength={30}
            placeholder={i18n.t(
              'orderAggregators:common:order.kanaPlaceholder'
            )}
            notes={[i18n.t('orderAggregators:common:order.kanaNotes')]}
            onBlur={value => handleBlurKana(form, value)}
          />
          <div className="postcode-wrapper">
            <TextField
              type="tel"
              name="receiverPostcode"
              className="input-postcode"
              validate={value =>
                composeValidators(required, postcodePattern, justLength(7))(
                  value
                )
              }
              label={i18n.t('orderAggregators:common:order.postcode')}
              maxLength={7}
              placeholder={i18n.t(
                'orderAggregators:common:order.postcodePlaceholder'
              )}
              required={true}
              onBlur={value => handleBlurPostcode(form, value)}
              onChange={() => handleChangePostCode(form)}
              onKeyDown={event => allowExactKeysOnly(event, /[^-]/)}
            />
            <Button
              buttonType="F"
              type="button"
              onClick={() => handleClickInsertAddress(form, fetchAddress)}
            >
              {i18n.t('orderAggregators:common:order.postcodeInput')}
            </Button>
          </div>
          <SelectField
            name="receiverPrefecture"
            validate={value => required(value)}
            label={i18n.t('orderAggregators:common:order.prefecture')}
            required={true}
            options={Prefectures}
          />
          <TextField
            name="receiverCity"
            validate={value => composeValidators(required)(value)}
            label={i18n.t('orderAggregators:common:order.city')}
            maxLength={20}
            required={true}
            placeholder={i18n.t(
              'orderAggregators:common:order.cityPlaceholder'
            )}
            notes={[i18n.t('orderAggregators:common:order.cityNotes')]}
          />
          <TextField
            name="receiverBuilding"
            label={i18n.t('orderAggregators:common:order.buildings')}
            maxLength={20}
            placeholder={i18n.t(
              'orderAggregators:common:order.buildingsPlaceholder'
            )}
            notes={[i18n.t('orderAggregators:common:order.buildingsNotes')]}
          />
          <TextField
            name="receiverCompany"
            label={i18n.t('orderAggregators:common:order.company')}
            maxLength={20}
            placeholder={i18n.t(
              'orderAggregators:common:order.companyPlaceholder'
            )}
            notes={[i18n.t('orderAggregators:common:order.companyNotes')]}
          />
          <TextField
            name="receiverTelephone"
            type="tel"
            validate={value => composeValidators(required, mustBeNumber)(value)}
            label={i18n.t('orderAggregators:common:order.tel')}
            maxLength={12}
            required={true}
            placeholder={i18n.t('orderAggregators:common:order.telPlaceholder')}
            onBlur={value =>
              handleBlurNumbers(form, 'receiverTelephone', value)
            }
            onKeyDown={event => allowExactKeysOnly(event, /[^-]/)}
          />
        </React.Fragment>
      )}
    </div>
  </div>
);

const DialogFooter = ({ handleSubmit, invalid, close, isConfirm }) => (
  <div className="footer-btn-wrapper">
    {isConfirm ? (
      <Button buttonType="B" type="button" onClick={close} disabled={invalid}>
        {i18n.t('common:ok')}
      </Button>
    ) : (
      <Button
        buttonType="B"
        type="button"
        onClick={handleSubmit}
        disabled={invalid}
      >
        {i18n.t('common:save')}
      </Button>
    )}
  </div>
);

class receiverDialog extends React.Component {
  constructor(props) {
    super(props);

    this.state = {};
  }

  render() {
    const {
      className,
      onSubmit,
      dialogRef,
      initialValues,
      fetchAddress,
      referringDataTemplate,
      isConfirm
    } = this.props;

    return (
      <div className={className}>
        <FormDialogrenderer
          title={i18n.t('orderAggregators:common:order.receiverDialogTitle')}
          innerRef={dialogRef}
          renderBody={({ form }) => (
            <DialogBody
              fetchAddress={fetchAddress}
              form={form}
              referringDataTemplate={referringDataTemplate}
              isConfirm={isConfirm}
            />
          )}
          renderFooter={props => (
            <DialogFooter {...props} isConfirm={isConfirm} />
          )}
          initialValues={initialValues}
          onSubmit={onSubmit}
          showCloseAlert={!isConfirm}
        />
      </div>
    );
  }
}

const commonStyle = css`
  .footer-btn-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  .image-wrapper {
    text-align: center;
  }
`;

const pcStyle = CustomMedia.greaterThan('umedium')`
  .receiver {
    padding: 40px 32px 0;
  }

  .image-wrapper {
    margin-bottom: 40px;
  }

  .input-postcode {
    margin-bottom: 16px;
  }

  .postcode-wrapper {
    margin-bottom: 40px;
  }

  .order-content {
    margin: 40px 0;
  }
`;

const spStyle = CustomMedia.lessThan('medium')`
  .image-wrapper {
    margin: 24px 0;
  }

  .postcode-wrapper {
    margin-bottom: 32px;

    ${TextField} {
      margin-bottom: 16px;
    }

    button {
      display: flex;
      justify-content: center;
      margin: 0 auto;
      align-items: center;
    }
  }

  .order-content {
    margin: 24px 16px 0;
  }
`;

export default applyStyles(receiverDialog)(commonStyle, pcStyle, spStyle);



// WEBPACK FOOTER //
// ./src/components/OrderAggregators/components/ReceiverDialog.js